import StyledButton from "components/shared/StyledButton";
import AgreementStatus from "components/shared/TermDetail/AgreementStatus";
import TitleSeparation from "components/shared/TitleSeparation";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Agreement } from "store/agreementsStore";
import { AgreementSignatureStatus, fetchAgreementPreview } from "store/agreementsStore";
import { useAppDispatch } from "store/store";

import { Spinner } from "../Spinner";

interface Props {
    agreements: Agreement[];
    resourceType: "term" | "article" | "frameworkContract";
    resourceId: number;
    // Parent id for the back-navigation on the Contracts page (beneficiaryId for term/frameworkContract, titleId for article).
    parentId?: number;
    // Term only: lets the Contracts page also list multi-signature agreements the term's beneficiary signs.
    titleId?: number;
    // Term only: the term's work, co-signers being found on the same work.
    workId?: number;
    // Slot so callers can pass a ButtonMenu when a single button is not enough (multi-signatory flow).
    primaryAction?: ReactNode;
    // Show a single spinner until all dependent data (agreements list + primary-action data) is ready.
    loading?: boolean;
}

const InlineCounter = ({ count, label }: { count: number; label: string }) => {
    const isZero = count === 0;
    return (
        <div className="flex items-baseline gap-1.5">
            <span
                className={isZero ? "text-foreground-disabled" : "text-ink"}
                style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: 15,
                    lineHeight: 1,
                    fontWeight: 600,
                }}
            >
                {count}
            </span>
            <span
                className="text-foreground"
                style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: 10,
                    lineHeight: 1,
                    fontWeight: 500,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                }}
            >
                {label}
            </span>
        </div>
    );
};

const ContractsCounterBlock = ({
    agreements,
    resourceType,
    resourceId,
    parentId,
    titleId,
    workId,
    primaryAction,
    loading,
}: Props) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [showStats, setShowStats] = useState(false);

    const counts = useMemo(() => {
        let signed = 0;
        let sent = 0;
        let draft = 0;
        let refused = 0;
        for (const a of agreements) {
            switch (a.signedStatus) {
                case AgreementSignatureStatus.SIGNED:
                    signed++;
                    break;
                case AgreementSignatureStatus.SENT:
                    sent++;
                    break;
                case AgreementSignatureStatus.REFUSED:
                    refused++;
                    break;
                case AgreementSignatureStatus.TO_SIGN:
                    draft++;
                    break;
            }
        }
        return { signed, sent, draft, refused, total: agreements.length };
    }, [agreements]);

    const handleOpenContracts = () => {
        const params = new URLSearchParams();
        const resourceParam =
            resourceType === "term" ? "termId" : resourceType === "article" ? "articleId" : "frameworkContractId";
        params.set(resourceParam, String(resourceId));
        if (parentId) params.set(resourceType === "article" ? "titleId" : "beneficiaryId", String(parentId));
        if (resourceType === "term" && titleId) params.set("titleId", String(titleId));
        if (resourceType === "term" && workId) params.set("workId", String(workId));
        navigate(`/agreements?${params.toString()}`);
    };

    if (loading) {
        return (
            <div className="flex min-h-[180px] items-center justify-center py-2">
                <Spinner />
            </div>
        );
    }

    const singleAgreement = agreements.length === 1 ? agreements[0] : null;

    const handleDownloadSingle = () => {
        if (!singleAgreement) return;
        dispatch(
            fetchAgreementPreview({
                filename: singleAgreement.filename?.split(".")[0] ?? "Contrat",
                agreementId: +(singleAgreement.id ?? -1),
            }),
        );
    };

    const isMulti = counts.total >= 2;

    return (
        <div className="font-poppins flex flex-col gap-3">
            <TitleSeparation
                title={t("ContractsCounterBlock.Title")}
                titleStyle={{ fontSize: "0.9rem" }}
                description={counts.total === 0 ? t("ContractsCounterBlock.SubtitleEmpty") : undefined}
            />
            {isMulti && (
                <p
                    className="text-foreground"
                    style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 12,
                        lineHeight: 1.4,
                        fontWeight: 400,
                        margin: 0,
                        marginTop: -4,
                    }}
                >
                    {t("ContractsCounterBlock.Subtitle", { count: counts.total })}
                    {t("ContractsCounterBlock.SignedCount", { count: counts.signed })}
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowStats((v) => !v)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setShowStats((v) => !v);
                            }
                        }}
                        className="text-ink"
                        style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlineOffset: 2,
                        }}
                    >
                        {showStats ? t("ContractsCounterBlock.HideDetails") : t("ContractsCounterBlock.ShowDetails")}
                    </span>
                </p>
            )}
            {isMulti && showStats && (
                <div className="bg-backgroundZ1 border-management-border-grey flex w-full items-center justify-around rounded-[3px] border px-4 py-1.5">
                    <InlineCounter count={counts.signed} label={t("ContractsCounterBlock.Signed")} />
                    <InlineCounter count={counts.sent} label={t("ContractsCounterBlock.Sent")} />
                    <InlineCounter count={counts.draft} label={t("ContractsCounterBlock.Draft")} />
                    <InlineCounter count={counts.refused} label={t("ContractsCounterBlock.Refused")} />
                </div>
            )}
            {singleAgreement && <AgreementStatus handleDownload={handleDownloadSingle} agreement={singleAgreement} />}
            <div className="flex gap-1.5">
                <StyledButton buttonType="outlined" fullWidth onClick={handleOpenContracts}>
                    {t("ContractsCounterBlock.OpenInContracts")}
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M14 5h5v5" />
                        <path d="M19 5L10 14" />
                        <path d="M19 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6" />
                    </svg>
                </StyledButton>
                {primaryAction}
            </div>
        </div>
    );
};

export default ContractsCounterBlock;
